import { configureStore } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

import { agentActions, agentReducer } from "./reducers/agentSlice";
import { agentTemplateActions, agentTemplateReducer } from "./reducers/agentTemplateSlice";
import { agentDeploymentActions, agentDeploymentReducer } from "./reducers/agentDeploymentSlice";
import { agentEnvironmentActions, agentEnvironmentReducer } from "./reducers/agentEnvironmentSlice";
import { agentEnvironmentVariableReducer } from "./reducers/agentEnvironmentVariableSlice";
import { agentExtractionActions, agentExtractionReducer } from "./reducers/agentExtractionSlice";
import { organizationSnippetActions, organizationSnippetReducer } from "./reducers/organizationSnippet";
import { evaluatorActions, evaluatorReducer } from "./reducers/evaluatorSlice";
import { agentFunctionActions, agentFunctionReducer } from "./reducers/agentFunctionSlice";
import { agentJudgeRulingActions, agentJudgeRulingReducer } from "./reducers/agentJudgeRulingSlice";
import { agentSessionActions, agentSessionReducer } from "./reducers/agentSessionSlice";
import { agentSessionEventActions, agentSessionEventReducer } from "./reducers/agentSessionEventSlice";
import { agentEvaluatorAssignmentActions, agentEvaluatorAssignmentReducer } from "./reducers/agentEvaluatorAssignmentSlice";
import { agentAnalyticsReducer } from "./reducers/agentAnalyticsSlice";
import { sessionTraceReducer } from "./reducers/sessionTraceSlice";
import { usageReducer } from "./reducers/usageSlice";
import {
  agentEvaluationReducer,
  agentEvaluationActions,
  sessionEvaluationReducer,
  sessionEvaluationActions,
  evaluatorEvaluationReducer,
  evaluatorEvaluationActions,
} from "./reducers/evaluationSlices";
import { featureReducer } from "./reducers/featureSlice";
import { organizationActions, organizationReducer } from "./reducers/organizationSlice";
import { organizationInvitesActions, organizationInvitesReducer } from "./reducers/organizationInvitesSlice";
import { organizationMembersActions, organizationMembersReducer } from "./reducers/organizationMemberSlice";
import { organizationTelephonyReducer } from "./reducers/organizationTelephonySlice";
import { organizationPhoneNumberActions, organizationPhoneNumberReducer } from "./reducers/organizationPhoneNumberSlice";
import { organizationCustomVoicesActions, organizationCustomVoicesReducer } from "./reducers/organizationCustomVoicesSlice";
import { organizationSecretsActions, organizationSecretsReducer } from "./reducers/organizationSecretsSlice";
import { organizationVariablesActions, organizationVariablesReducer } from "./reducers/organizationVariablesSlice";
import { organizationServiceAccountsActions, organizationServiceAccountsReducer } from "./reducers/organizationServiceAccountsSlice";
import { organizationServiceAccountTokensReducer } from "./reducers/organizationServiceAccountTokensSlice";
import { permissionRegistryReducer } from "./reducers/permissionRegistrySlice";
import { simulationAgentActions, simulationAgentReducer } from "./reducers/simulationAgentSlice";
import { sessionActions, sessionReducer } from "./reducers/sessionSlice";
import testNotificationsReducer from "./reducers/testNotificationsSlice";
import { userActions, userReducer } from "./reducers/userSlice";
import notificationReducer from "./reducers/notificationSlice";
import notificationPreferenceReducer from "./reducers/notificationPreferenceSlice";
import { customMetricsReducer } from "./reducers/customMetricsSlice";
import { experimentsReducer } from "./reducers/experimentsSlice";
import { environmentActions, environmentReducer } from "./reducers/environmentSlice";
import { releaseActions, releaseReducer } from "./reducers/releaseSlice";
import { entrypointActions, entrypointReducer } from "./reducers/entrypointSlice";

export type RootState = ReturnType<typeof store.getState>;
export type EntityState = Omit<RootState, "session" | "feature">;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = useDispatch.withTypes<AppDispatch>();
export const useAppSelector = useSelector.withTypes<RootState>();

export const store = configureStore({
  reducer: {
    agent: agentReducer,
    agentTemplate: agentTemplateReducer,
    agentDeployment: agentDeploymentReducer,
    agentEnvironment: agentEnvironmentReducer,
    agentEnvironmentVariable: agentEnvironmentVariableReducer,
    agentExtraction: agentExtractionReducer,
    agentEvaluatorAssignment: agentEvaluatorAssignmentReducer,
    agentEvaluation: agentEvaluationReducer,
    agentFunction: agentFunctionReducer,
    agentJudgeRuling: agentJudgeRulingReducer,
    agentSession: agentSessionReducer,
    agentSessionEvent: agentSessionEventReducer,
    agentAnalytics: agentAnalyticsReducer,
    sessionTrace: sessionTraceReducer,
    evaluatorEvaluation: evaluatorEvaluationReducer,
    sessionEvaluation: sessionEvaluationReducer,
    usage: usageReducer,
    evaluator: evaluatorReducer,
    feature: featureReducer,
    organization: organizationReducer,
    simulationAgent: simulationAgentReducer,
    organizationVariables: organizationVariablesReducer,
    organizationServiceAccounts: organizationServiceAccountsReducer,
    organizationServiceAccountTokens: organizationServiceAccountTokensReducer,
    permissionRegistry: permissionRegistryReducer,
    organizationInvites: organizationInvitesReducer,
    organizationMembers: organizationMembersReducer,
    organizationTelephony: organizationTelephonyReducer,
    organizationPhoneNumbers: organizationPhoneNumberReducer,
    organizationSecrets: organizationSecretsReducer,
    organizationCustomVoices: organizationCustomVoicesReducer,
    organizationSnippets: organizationSnippetReducer,
    session: sessionReducer,
    testNotifications: testNotificationsReducer,
    user: userReducer,
    notifications: notificationReducer,
    notificationPreferences: notificationPreferenceReducer,
    customMetrics: customMetricsReducer,
    experiments: experimentsReducer,
    environment: environmentReducer,
    release: releaseReducer,
    entrypoint: entrypointReducer,
  },
});

export const getActionsByName = (entityName: keyof EntityState) => {
  switch (entityName) {
    case "agent":
      return agentActions;
    case "agentTemplate":
      return agentTemplateActions;
    case "agentDeployment":
      return agentDeploymentActions;
    case "agentExtraction":
      return agentExtractionActions;
    case "agentEnvironment":
      return agentEnvironmentActions;
    case "agentEvaluatorAssignment":
      return agentEvaluatorAssignmentActions;
    case "agentFunction":
      return agentFunctionActions;
    case "agentEvaluation":
      return agentEvaluationActions;
    case "agentJudgeRuling":
      return agentJudgeRulingActions;
    case "agentSession":
      return agentSessionActions;
    case "agentSessionEvent":
      return agentSessionEventActions;
    case "sessionEvaluation":
      return sessionEvaluationActions;
    case "evaluator":
      return evaluatorActions;
    case "evaluatorEvaluation":
      return evaluatorEvaluationActions;
    case "organization":
      return organizationActions;
    case "organizationInvites":
      return organizationInvitesActions;
    case "organizationMembers":
      return organizationMembersActions;
    case "organizationPhoneNumbers":
      return organizationPhoneNumberActions;
    case "organizationCustomVoices":
      return organizationCustomVoicesActions;
    case "organizationSecrets":
      return organizationSecretsActions;
    case "organizationVariables":
      return organizationVariablesActions;
    case "organizationServiceAccounts":
      return organizationServiceAccountsActions;
    case "organizationSnippets":
      return organizationSnippetActions;
    case "simulationAgent":
      return simulationAgentActions;
    case "user":
      return userActions;
    case "environment":
      return environmentActions;
    case "release":
      return releaseActions;
    case "entrypoint":
      return entrypointActions;
    default:
      throw new Error(`Unknown entity name: ${entityName}`);
  }
};

export const onAuthenticationError = () => {
  store.dispatch(sessionActions.getSession());
};
